import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Container,
  Paper,
  InputBase,
  Button
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import { useIntl } from 'react-intl';
import { useAuthToken } from "../TokenContext";
import { useSelector } from "react-redux";
// Main wrapper that takes full viewport height
const PageWrapper = styled(Box)(({ theme, collapsed }) => ({
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

// Animation keyframes remain the same
const pulseAnimation = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`;
// Add this with your other styled components
const ApiResponseBubble = styled(Box)(({ status }) => ({
  maxWidth: '90%',
  padding: '12px 16px',
  borderRadius: '12px',
  backgroundColor: status === 200 ? 'rgba(var(--artemy-green-rgb), 0.1)' : 'rgba(var(--artemy-primary-rgb), 0.1)',
  alignSelf: 'center',
  fontFamily: 'Poppins, Medium',
  fontSize: '14px',
  lineHeight: '1.6',
  marginTop: '8px',
  marginBottom: '8px',
  color: status === 200 ? 'var(--artemy-green)' : 'var(--artemy-primary)',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  border: status === 200 ? '1px solid var(--artemy-green)' : '1px solid var(--artemy-primary)',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '&::before': {
    content: '""',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: status === 200 ? 'var(--artemy-green)' : 'var(--artemy-primary)'
  }
}));
const SendResultsButton = styled(Button)(() => ({
  margin: '16px auto',
  backgroundColor: 'var(--artemy-primary)',
  color: '#fff',
  fontFamily: 'Outfit, sans-serif',
  borderRadius: '8px',
  padding: '10px 24px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'var(--artemy-primary-hover)',
  },
  '&.Mui-disabled': {
    backgroundColor: 'rgba(255,255,255,0.1)',
    color: 'var(--artemy-text-secondary)'
  }
}));
const LoadingIndicator = styled(Box)(() => ({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  padding: '12px 16px',
  maxWidth: '90%',
  alignSelf: 'flex-start',
}));

const LoadingDot = styled(Box)(() => ({
  width: '8px',
  height: '8px',
  backgroundColor: 'var(--artemy-primary)',
  borderRadius: '50%',
  animation: `${pulseAnimation} 1.4s infinite`,
  '&:nth-of-type(2)': {
    animationDelay: '0.2s',
  },
  '&:nth-of-type(3)': {
    animationDelay: '0.4s',
  }
}));

const MainContainer = styled(Container)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  position: 'relative',
  paddingTop: '2rem',
  paddingBottom: '2rem',
}));

const StyledPaper = styled(Paper)(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: 'var(--artemy-bg-elevated)',
  borderRadius: '16px',
  overflow: 'hidden',
  position: 'relative',
  border: '1px solid var(--artemy-border)',
  boxShadow: 'none',
  transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
}));

const ChatContainer = styled(Box)(() => ({
  flex: 1,
  overflowY: 'auto',
  padding: '24px',
  paddingBottom: '80px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  backgroundColor: 'transparent',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'var(--artemy-border)',
    borderRadius: '4px',
    '&:hover': {
      background: 'var(--artemy-primary)',
    },
  }
}));

const MessageBubble = styled(Box)(({ isUser }) => ({
  maxWidth: '90%',
  padding: '16px 20px',
  borderRadius: '16px',
  backgroundColor: isUser ? 'rgba(var(--artemy-primary-rgb), 0.15)' : 'rgba(255,255,255,0.03)',
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  fontFamily: 'Poppins, Medium',
  fontSize: '14px',
  lineHeight: '1.6',
  whiteSpace: 'pre-wrap',
  color: 'var(--artemy-text-primary)',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  border: isUser ? '1px solid var(--artemy-primary)' : '1px solid var(--artemy-border)',

  '& .category': {
    fontWeight: 600,
    color: 'var(--artemy-primary)',
    marginTop: '16px',
    marginBottom: '8px',
    '&:first-of-type': {
      marginTop: 0
    }
  },

  '& ul': {
    margin: '8px 0',
    paddingLeft: '20px',
    listStyle: 'none',
  },

  '& li': {
    position: 'relative',
    padding: '8px 0',
    paddingLeft: '24px',
    marginBottom: '4px',
    '&:before': {
      content: '"•"',
      position: 'absolute',
      left: '8px',
      color: 'var(--artemy-primary)',
      fontWeight: 'bold',
    }
  }
}));

const InputContainer = styled(Box)(() => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  padding: '16px 24px',
  backgroundColor: 'var(--artemy-bg-elevated)',
  borderTop: '1px solid var(--artemy-border)',
  zIndex: 10,
  backdropFilter: 'blur(10px)',
}));

const StyledInputContainer = styled(Paper)(() => ({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 16px',
  borderRadius: '12px',
  backgroundColor: 'rgba(255,255,255,0.03)',
  color: 'var(--artemy-text-primary)',
  border: '1px solid var(--artemy-border)',
  transition: 'all 0.2s ease',
  boxShadow: 'none',
  '&:hover': {
    border: '1px solid var(--artemy-primary)',
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  '&:focus-within': {
    border: '1px solid var(--artemy-primary)',
    boxShadow: '0 0 0 2px rgba(var(--artemy-primary-rgb), 0.2)',
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
}));

const StyledInput = styled(InputBase)(() => ({
  flex: 1,
  fontFamily: 'Poppins, Medium',
  fontSize: '14px',
  '& input': {
    color: 'var(--artemy-text-primary)',
  },
  '& input::placeholder': {
    color: 'var(--artemy-text-secondary)',
  },
  '& textarea': {
    color: 'var(--artemy-text-primary)',
  },
}));

const UserQuery = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  maxWidth: '800px',
  margin: '0 auto 16px',
  padding: '16px',
  '& > div': {
    backgroundColor: 'rgba(var(--artemy-primary-rgb), 0.15)',
    border: '1px solid var(--artemy-primary)',
    borderRadius: '24px',
    padding: '12px 24px',
    color: 'var(--artemy-text-primary)',
    fontSize: '14px',
    fontFamily: 'Poppins, Medium',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  }
}));

const SendButton = styled(IconButton)(() => ({
  padding: '8px',
  color: 'var(--artemy-primary)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(var(--artemy-primary-rgb), 0.15)',
    color: 'var(--artemy-primary-hover)',
  },
  '&.Mui-disabled': {
    color: 'var(--artemy-text-secondary)',
  }
}));

const OpenAi = ({ collapsed }) => {
  const [inputText, setInputText] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const chatEndRef = useRef(null);
  const [showSendButton, setShowSendButton] = useState(false);
  const intl = useIntl();
  const token = useAuthToken();
  const { user } = useSelector(state => state.auth);
  const [apiResponse, setApiResponse] = useState(null);
  const [sendResultsLoading, setSendResultsLoading] = useState(false);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleInputChange = (e) => {
    setInputText(e.target.value);
  };
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });

    if (messages.length > 0) {
      setShowSendButton(true);
    } else {
      setShowSendButton(false);
    }
  }, [messages]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputText.trim() || isLoading) return;

    const userMessage = inputText;
    setInputText('');
    setIsLoading(true);

    try {
      const response = await fetch(process.env.REACT_APP_CLIENT_JIRAAUTOMATIONNEW, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          conversation_history: messages,
          user_input: userMessage,
          manager_id: user.data[0].emp_id,
          ...(user.data[0].type === "Candidate" && { talent_id: user.data[0].c_id }),
          ...(user.data[0].type === "stakeholder" && { stakeholder_id: user.data[0].stakeholder_id })
        })

      });

      const data = await response.json();

      if (data.latest_response) {
        setMessages(prevMessages => [
          ...prevMessages,
          {
            user: userMessage,
            assistant: data.latest_response
          }
        ]);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };


  const formatMessage = (text) => {
    if (!text) return '';

    const categories = {
      'Input Fields': [],
      'Validation': [],
      'User Experience': [],
      'Security': []
    };

    const lines = text.split('\n').map(line => line.trim()).filter(line => line);

    // If it's not a test case response, return the text as is
    if (!lines[0]?.toLowerCase().includes('test case')) {
      return text;
    }

    // Process test cases and categorize them
    lines.slice(1).forEach(line => {
      const testCase = line.replace(/^Test Case /, '').trim();

      if (testCase.toLowerCase().includes('field') || testCase.toLowerCase().includes('input')) {
        categories['Input Fields'].push(testCase);
      } else if (testCase.toLowerCase().includes('valid') || testCase.toLowerCase().includes('error')) {
        categories['Validation'].push(testCase);
      } else if (testCase.toLowerCase().includes('user') || testCase.toLowerCase().includes('responsive')) {
        categories['User Experience'].push(testCase);
      } else if (testCase.toLowerCase().includes('security') || testCase.toLowerCase().includes('protect')) {
        categories['Security'].push(testCase);
      }
    });
    return (
      <>
        {Object.entries(categories).map(([category, testCases]) =>
          testCases.length > 0 && (
            <React.Fragment key={category}>
              <div className="category">{category}</div>
              <ul>
                {testCases.map((testCase, index) => (
                  <li key={index}>{testCase}</li>
                ))}
              </ul>
            </React.Fragment>
          )
        )}
      </>
    );
  };


  const handleSendResults = async () => {
    if (messages.length === 0) return;
    setSendResultsLoading(true); // Start loading
    const latestMessage = messages[messages.length - 1];
    const latestResponse = latestMessage.assistant;
    const userInput = latestMessage.user;

    try {
      const payload = {
        manager_id: user.data[0].emp_id,
        latest_response: latestResponse,
        user_input: userInput
      };

      const apiUrl = process.env.REACT_APP_CLIENT_JIRASTORYCREATEMANAGER;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      setApiResponse(data);
    } catch (error) {
      console.error('Error sending results:', error);
      setApiResponse({
        statusCode: 400,
        body: 'Failed to create issue. Please try again.'
      });
    } finally {
      setSendResultsLoading(false); // End loading
    }
  };

  // Update your ChatContainer to include the API response

  return (
    <PageWrapper collapsed={collapsed}>
      <MainContainer maxWidth="lg">
        {messages.length === 0 && (
          <UserQuery>
            <Box>{intl.formatMessage({ id: 'podAssist' })}</Box>
          </UserQuery>
        )}

        <StyledPaper elevation={0}>
          <ChatContainer>
            {messages.map((message, index) => (
              <React.Fragment key={index}>
                <MessageBubble isUser>
                  {message.user}
                </MessageBubble>
                <MessageBubble>
                  {formatMessage(message.assistant)}
                </MessageBubble>
              </React.Fragment>
            ))}

            {!sendResultsLoading && apiResponse && (
              <ApiResponseBubble status={apiResponse.statusCode}>
                {apiResponse.body}
              </ApiResponseBubble>
            )}
            {isLoading && (
              <LoadingIndicator>
                <LoadingDot />
                <LoadingDot />
                <LoadingDot />
              </LoadingIndicator>
            )}
            <div ref={chatEndRef} />
          </ChatContainer>

          <InputContainer>
            <form onSubmit={handleSubmit}>
              <StyledInputContainer elevation={0}>
                <StyledInput
                  fullWidth
                  multiline
                  maxRows={4}
                  value={inputText}
                  onChange={handleInputChange}
                  placeholder={intl.formatMessage({ id: 'enterStoryDescription' })}
                  disabled={isLoading}
                />
                <SendButton
                  type="submit"
                  disabled={!inputText.trim() || isLoading}
                  onClick={handleSubmit}
                >
                  <SendIcon />
                </SendButton>
              </StyledInputContainer>
            </form>
          </InputContainer>

        </StyledPaper>
        {showSendButton && (
          <SendResultsButton onClick={handleSendResults}>
            Send Results
          </SendResultsButton>
        )}
      </MainContainer>
    </PageWrapper>
  );
};

export default OpenAi;